import React, { useState } from 'react';
import { DollarSignIcon, InfoIcon, BookIcon, CalendarIcon } from 'lucide-react';
import { Budget } from './BudgetManagement';
import { BudgetStatusBadge } from './BudgetStatusBadge';
import { BudgetRules } from './BudgetRules';
import { formatCurrency } from '../../utils/currencyFormatter';
interface BudgetCardProps {
  budget: Budget;
  locale: string;
}
export function BudgetCard({
  budget,
  locale
}: BudgetCardProps) {
  // Calculate percentage of budget used
  const percentUsed = Math.min(100, Math.round((budget.totalAmount - budget.remainingAmount) / budget.totalAmount * 100));
  const percentRemaining = 100 - percentUsed;
  // Determine color based on remaining percentage
  const getProgressColor = () => {
    if (percentRemaining > 50) return 'bg-green-500';
    if (percentRemaining > 20) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  // Format dates
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  return <div className="bg-white rounded-lg overflow-hidden" data-id="element-8359">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-8360">
        <div className="md:col-span-2" data-id="element-8361">
          <div className="space-y-4" data-id="element-8362">
            <div data-id="element-8363">
              <h4 className="text-lg font-medium text-gray-900" data-id="element-8364">
                {budget.name}
              </h4>
              {budget.description && <p className="mt-1 text-sm text-gray-500" data-id="element-8365">
                  {budget.description}
                </p>}
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-8366">
              <div data-id="element-8367">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-8368">
                  <BookIcon className="h-3 w-3 mr-1" data-id="element-8369" />
                  Budget Type
                </h5>
                <p className="mt-1 text-sm text-gray-900" data-id="element-8370">
                  {budget.budgetTypeName}
                </p>
              </div>
              <div data-id="element-8371">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-8372">
                  <CalendarIcon className="h-3 w-3 mr-1" data-id="element-8373" />
                  Period
                </h5>
                <p className="mt-1 text-sm text-gray-900" data-id="element-8374">
                  {formatDate(budget.startDate)} to {formatDate(budget.endDate)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-8375">
              <div data-id="element-8376">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-8377">
                  GL Code
                </h5>
                <p className="mt-1 text-sm font-mono text-gray-900" data-id="element-8378">
                  {budget.glCode}
                </p>
              </div>
              <div data-id="element-8379">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-8380">
                  Entity / Site
                </h5>
                <p className="mt-1 text-sm text-gray-900" data-id="element-8381">
                  {budget.entityName || 'Global'}
                  {budget.siteName && <span className="text-gray-500" data-id="element-8382"> / {budget.siteName}</span>}
                </p>
              </div>
            </div>
            <div data-id="element-8383">
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-8384">
                <DollarSignIcon className="h-3 w-3 mr-1" data-id="element-8385" />
                Budget Status
              </h5>
              <div className="mt-2 flex items-center" data-id="element-8386">
                <BudgetStatusBadge status={budget.status} data-id="element-8387" />
                <span className="ml-2 text-sm text-gray-500" data-id="element-8388">
                  Last updated:{' '}
                  {new Date(budget.updatedAt).toLocaleDateString(locale, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="md:col-span-1" data-id="element-8389">
          <div className="space-y-4" data-id="element-8390">
            <div data-id="element-8391">
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-8392">
                Budget Amount
              </h5>
              <div className="mt-1" data-id="element-8393">
                <div className="text-2xl font-semibold text-gray-900" data-id="element-8394">
                  {formatCurrency(budget.remainingAmount, budget.currency, locale)}
                </div>
                <div className="text-sm text-gray-500" data-id="element-8395">
                  remaining of{' '}
                  {formatCurrency(budget.totalAmount, budget.currency, locale)}
                </div>
              </div>
            </div>
            <div data-id="element-8396">
              <div className="flex justify-between text-sm" data-id="element-8397">
                <span data-id="element-8398">{percentRemaining}% remaining</span>
                <span data-id="element-8399">{percentUsed}% used</span>
              </div>
              <div className="mt-1 w-full bg-gray-200 rounded-full h-2.5" data-id="element-8400">
                <div className={`h-2.5 rounded-full ${getProgressColor()}`} style={{
                width: `${percentRemaining}%`
              }} data-id="element-8401"></div>
              </div>
            </div>
            <div className="mt-4" data-id="element-8402">
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-8403">
                <InfoIcon className="h-3 w-3 mr-1" data-id="element-8404" />
                Budget Rules
              </h5>
              <div className="mt-2" data-id="element-8405">
                <BudgetRules rulesJson={budget.rules} data-id="element-8406" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}